import { useMemo } from 'react'
import { useFormContext } from 'react-hook-form'
import { useNavigate, useSearchParams } from 'react-router-dom'

import { Button } from '@cms/components/data/Button'
import { useSnackbar } from '@cms/providers/SnackbarProvider'
import { generateStoryFormState, StoryFormData } from '@cms/utils/forms/story'
import { generateSearchParam, getEditorStoryVersion } from '@cms/utils/helpers'
import { useInterval } from '@cms/utils/hooks/useInterval'
import { IUpdateStoryDto } from '@cms/utils/interfaces/dto.interface'
import { StoriesPath, TemplatesPath } from '@cms/utils/routes'
import { useAppDispatch, useAppSelector } from '@cms/utils/store/hooks'
import { saveStoryAsyncAction } from '@cms/utils/store/slices/editor.slice'
import { ISystemStory } from '@dl/declarations'
import { useLexicalComposerContext } from '@lexical/react/LexicalComposerContext'
import { lexicalToContentElements } from '../../../components/editor/utils/helpers'

// const ALLOWED_USER_TEAMS: Array<string> = [
//     TEAMS.NEWSROOM.SLUG,
//     TEAMS.RESEARCH.SLUG,
// ]

// const ReviewStoryVersionMenu = () => {
//     const dispatch = useAppDispatch()
//     const { state, currentVersion } = useAppSelector((state) => state.editor)

//     const methods = useForm<StoryReviewFormData>({
//         resolver: yupResolver(reviewSchema),
//         mode: 'all',
//         defaultValues: {
//             assigned_to: [],
//             comment: ''
//         }
//     })

//     const { handleSubmit, formState } = methods;

//     const { data: allAuthors } = authorsLookupsQuery({
//         team: ALLOWED_USER_TEAMS.join(','),
//         is_active: true,
//     })

//     const authorsLookupsToOptions = useMemo(() => {
//         const mapped = mapLookupsToOptions(allAuthors || [], 'name', (item) => item.role)
//         return mapped
//     }, [allAuthors])

//     const buttonState = useMemo(() => {
//         if (state !== 'idle') {
//             return {
//                 type: 'disabled',
//                 tooltip: 'Story is being saved, please wait'
//             }
//         }

//         if (currentVersion?.reviews?.length === 5) {
//             return {
//                 type: 'disabled',
//                 tooltip: 'Maximum number of reviews reached for this story version'
//             }
//         }

//         return {
//             type: 'enabled',
//             tooltip: 'Review story version'
//         }
//     }, [state, currentVersion])

//     const handleSubmitReview = (data: StoryReviewFormData) => {
//         dispatch(reviewStoryVersionAsyncAction({
//             id: currentVersion?._id as string,
//             data: {
//                 assigned_to: data.assigned_to,
//                 comment: data.comment
//             }
//         }))
//             .then((payload) => {
//                 if (payload.meta.requestStatus === 'fulfilled') {
//                     methods.reset()

//                     toastSuccess({
//                         title: 'Your review has been submitted',
//                         actionText: 'OKAY'
//                     }, {
//                         toasterId: TOASTER_IDS.STORY_ACTIONS,
//                     })
//                 }
//                 else {
//                     toastError({
//                         title: 'Error submitting review',
//                         actionText: 'OKAY',
//                     }, {
//                         toasterId: TOASTER_IDS.STORY_ACTIONS,
//                     })
//                 }
//             })
//             .catch(() => {
//                 toastError({
//                     title: 'Error submitting review',
//                     actionText: 'OKAY',
//                 }, {
//                     toasterId: TOASTER_IDS.STORY_ACTIONS,
//                 })
//             })
//     }

//     return (
//         <Menu
//             mainLabel={{
//                 component: (
//                     <Button
//                         as='button'
//                         tooltip={buttonState.tooltip}
//                         inputVariant='deep_ocean_text_white_bg'
//                         disabled={buttonState.type === 'disabled'}
//                         isLoading={state === 'saving'}
//                         inputSize='xs'
//                     >
//                         Review
//                     </Button>
//                 ),
//             }}
//             modal={{
//                 component: (
//                     <FormProvider {...methods}>
//                         <form
//                             onSubmit={handleSubmit(handleSubmitReview)}
//                             className='min-w-96 p-4 flex flex-col gap-2'
//                         >
//                             <ControlledDropdown
//                                 name='assigned_to'
//                                 label='Assign to'
//                                 inputSize='full'
//                                 maxChipsCount={1}
//                                 searchOptions={{
//                                     allow: false
//                                 }}

//                                 options={authorsLookupsToOptions}
//                                 valueKey='_id'

//                                 allowMultiSelect
//                             />

//                             <ControlledTextArea
//                                 description='Comment'
//                                 name='comment'
//                                 inputSize='full'
//                                 rows={8}
//                             />

//                             <Button
//                                 type='submit'
//                                 disabled={!formState.isValid || !formState.isDirty || state !== 'idle'}
//                                 isLoading={state === 'saving'}
//                                 inputVariant='blue_text_white_bg'
//                                 inputSize='full'
//                             >
//                                 Save
//                             </Button>
//                         </form>
//                     </FormProvider>
//                 ),
//                 position: 'absolute_bottom'
//             }}
//         />
//     )
// }

// const AUTO_SAVE_INTERVAL_MS = 60_000 // 1 minute
const AUTO_SAVE_INTERVAL_MS = 300_000 // 5 mins

interface SaveStoryButtonProps {
    buttonProps?: React.ComponentProps<typeof Button>;
}

export const SaveStoryButton: React.FC<SaveStoryButtonProps> = (props) => {
    const { buttonProps = {} } = props;

    const navigate = useNavigate()
    const dispatch = useAppDispatch()
    const { showSnackbar } = useSnackbar()

    const [editor] = useLexicalComposerContext();
    const { formState, getValues, reset, trigger, getFieldState } = useFormContext<StoryFormData>()
    const { story, mode, state, currentVersion, lastSavedVersionId, hasContentElementsChanged } = useAppSelector((state) => state.editor)

    const [searchParams, setSearchParams] = useSearchParams()
    const story_id = searchParams.get(generateSearchParam(StoriesPath, "id"))

    const buttonState = useMemo(() => {
        if (mode !== 'editing') {
            return {
                type: 'disabled',
                tooltip: 'Story is in readonly mode'
            }
        }

        const { invalid: isHeadlineInvalid, error: headlineError } = getFieldState('headline')

        if (isHeadlineInvalid) {
            return {
                type: 'disabled',
                tooltip: headlineError?.message || 'Invalid headline'
            }
        }

        const { invalid: isSubtypeInvalid, error: subtypeError } = getFieldState('subtype')

        if (isSubtypeInvalid) {
            return {
                type: 'disabled',
                tooltip: subtypeError?.message || 'Invalid subtype'
            }
        }

        if (
            Object.entries(formState.dirtyFields).length === 0
            &&
            !hasContentElementsChanged
        ) {
            return {
                type: 'disabled',
                tooltip: 'Make a change to save the story'
            }
        }

        return {
            type: 'enabled',
            tooltip: 'Save Story'
        }
    }, [formState, story, mode, hasContentElementsChanged])

    const handleSaveStory = (storyVersionId?: string) => {
        if (buttonState.type === 'disabled' || state !== 'idle') return;

        const values = getValues()
        const content_elements = lexicalToContentElements(editor)

        const payload: IUpdateStoryDto = {
            ...values,
            content_elements,
            story_version_id: storyVersionId
        }

        dispatch(saveStoryAsyncAction({
            id: story?._id,
            dto: payload
        }))
            .then((payload) => {
                if (payload.meta.requestStatus === 'fulfilled') {
                    const story = getEditorStoryVersion(payload.payload as ISystemStory);

                    // Handle Create flow, no _id in the search params
                    if (!story_id && story._id) {
                        setSearchParams((prev) => {
                            if (prev.has(generateSearchParam(TemplatesPath, "id"))) {
                                prev.delete(generateSearchParam(TemplatesPath, "id"))
                            }

                            prev.set(generateSearchParam(StoriesPath, "id"), story._id);
                            return prev;
                        });
                    }
                    else {
                        setSearchParams((prev) => {
                            prev.set(generateSearchParam("versions", "id"), story.saved_story_version);
                            return prev;
                        })

                        const state = generateStoryFormState(story)
                        reset(state)
                    }


                    showSnackbar('Your changes have been saved', 'success')
                }
                else {
                    const message = (payload as any)?.error?.message || 'An error occurred'
                    showSnackbar(`Error saving story: ${message}`, 'error')

                    setTimeout(() => {
                        // refresh page to reset to state
                        navigate(0)
                    }, 1000);
                }
            })
    }

    const handleAutoSaveStory = () => {
        if (currentVersion?._id === lastSavedVersionId) return;
        handleSaveStory(story?.saved_story_version)
    }

    const handleMouseOver = () => {
        trigger()
    }

    useInterval(handleAutoSaveStory, AUTO_SAVE_INTERVAL_MS)

    return (
        <Button
            tooltip={buttonState.tooltip}
            onClick={() => handleSaveStory()}
            inputVariant='white_text_blue_bg'
            disabled={buttonState.type === 'disabled' || state !== 'idle'}
            isLoading={state === 'saving'}
            onMouseOver={handleMouseOver}
            {...buttonProps}
        >
            {buttonProps.children || 'Save'}
        </Button>
    )
}
